/**
 * Phase 101fk.4 (Feb 2026) — Install PWA prompt + update banner.
 *
 * Bouton "Installer l'app" affiché quand Chrome/Edge/Samsung propose
 * l'installation (event `beforeinstallprompt`). iOS Safari : instructions
 * "Ajouter à l'écran d'accueil" (Safari ne supporte pas beforeinstallprompt).
 *
 * Bannière de mise à jour (event `sw-update`) : propose un rechargement doux.
 */
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Download, RefreshCw, X } from "lucide-react";
import { skipWaitingAndReload } from "@/lib/serviceWorkerRegistration";

// Utile pour distinguer iOS Safari (pas de beforeinstallprompt)
function isIosSafari() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const ios = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
  const safari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
  return ios && safari;
}

function isStandalone() {
  if (typeof window === "undefined") return false;
  const mq = window.matchMedia && window.matchMedia("(display-mode: standalone)").matches;
  return mq || window.navigator.standalone === true;
}

export function InstallPWAButton({ className = "" }) {
  const [deferred, setDeferred] = useState(null);
  const [dismissed, setDismissed] = useState(false);
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;
    if (localStorage.getItem("pwa-install-dismissed") === "1") {
      setDismissed(true);
    }
    const onBefore = (e) => {
      e.preventDefault();
      setDeferred(e);
    };
    window.addEventListener("beforeinstallprompt", onBefore);
    return () => window.removeEventListener("beforeinstallprompt", onBefore);
  }, []);

  if (isStandalone() || dismissed) return null;
  if (!deferred && !isIosSafari()) return null;

  const promptInstall = async () => {
    if (isIosSafari()) {
      setIosHint(true);
      return;
    }
    if (!deferred) return;
    deferred.prompt();
    try {
      const choice = await deferred.userChoice;
      if (choice?.outcome === "accepted") {
        setDeferred(null);
      }
    } catch {}
  };

  return (
    <>
      <Button
        onClick={promptInstall}
        size="sm"
        variant="outline"
        className={`h-8 border-[#1F4E3D] text-[#1F4E3D] hover:bg-[#EAF4EE] ${className}`}
        data-testid="pwa-install-btn"
        title="Installer l'app sur ton écran d'accueil (offline + accès rapide)"
      >
        <Download className="w-3.5 h-3.5 mr-1" /> Installer l'app
      </Button>
      {iosHint && (
        <div
          className="fixed bottom-4 left-4 right-4 z-50 bg-[#1F4E3D] text-white rounded-lg p-4 shadow-lg max-w-md mx-auto"
          data-testid="pwa-install-ios-hint"
        >
          <div className="flex items-start justify-between gap-2">
            <div className="text-sm">
              <b>Installer sur iPhone/iPad :</b>
              <ol className="list-decimal ml-5 mt-1 space-y-0.5 text-xs">
                <li>Appuie sur le bouton Partager en bas de Safari</li>
                <li>Fais défiler jusqu'à « Sur l'écran d'accueil »</li>
                <li>Appuie sur « Ajouter »</li>
              </ol>
            </div>
            <button
              onClick={() => {
                setIosHint(false);
                localStorage.setItem("pwa-install-dismissed", "1");
                setDismissed(true);
              }}
              className="text-white/80 hover:text-white"
              aria-label="Fermer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export function PWAUpdateBanner() {
  const [registration, setRegistration] = useState(null);

  useEffect(() => {
    const onUpdate = (e) => setRegistration(e.detail?.registration || null);
    window.addEventListener("sw-update", onUpdate);
    return () => window.removeEventListener("sw-update", onUpdate);
  }, []);

  if (!registration) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-50 bg-[#8C6B2F] text-white rounded-lg p-3 shadow-lg max-w-md mx-auto flex items-center gap-3"
      data-testid="pwa-update-banner"
    >
      <RefreshCw className="w-4 h-4 shrink-0" />
      <div className="text-sm flex-1">
        Nouvelle version disponible
      </div>
      <Button
        onClick={() => skipWaitingAndReload(registration)}
        size="sm"
        className="h-7 bg-white text-[#8C6B2F] hover:bg-[#FBF4E9]"
        data-testid="pwa-reload-btn"
      >
        Recharger
      </Button>
      <button
        onClick={() => setRegistration(null)}
        className="text-white/80 hover:text-white"
        aria-label="Reporter"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
